import React, { useEffect, useState, useMemo} from 'react';
import { Nav, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { NavLink, useLocation } from 'react-router-dom';
import * as Icon from 'react-bootstrap-icons';
import '../styles/MenuStyles.css';
import { useSelector, useDispatch } from 'react-redux';
import { fetchFlowsAnyConversationFromAPI } from '../Redux/FlowDucks';
import { fetchUnreadCountFromAPI } from '../Redux/NotificacionesDucks';

import { canAccess, isSuperAdmin } from '../Utilities/accessControl';

const getUserBusinessLineIds = () => {
    const cookies = document.cookie.split('; ');
    const perfilCookie = cookies.find(c => c.startsWith('perfil='));
    const perfil = perfilCookie ? decodeURIComponent(perfilCookie.split('=')[1]) : '';
    if (perfil === 'Super-Admin') return null; // null = todas
    const blCookie = cookies.find(c => c.startsWith('businessLineIds='));
    if (!blCookie) return [];
    return blCookie.split('=')[1].split(',').map(Number).filter(Boolean);
};

const Menu = () => {
    const dispatch = useDispatch();
    const [isExpanded, setIsExpanded] = useState(false);
    const [openSubMenu, setOpenSubMenu] = useState("");
    const location = useLocation();
    const flowList = useSelector(store => store.flow.flowMenu) || [];
    const unreadCount = useSelector(store => store.notificaciones?.unreadCount ?? 0);
    const userBLIds = getUserBusinessLineIds();

    useEffect(() => {
        dispatch(fetchFlowsAnyConversationFromAPI());
    }, []);

    useEffect(() => {
        if (!window.SHOW_SMART_TALKING) return;
        dispatch(fetchUnreadCountFromAPI());
        const interval = setInterval(() => dispatch(fetchUnreadCountFromAPI()), 30_000);
        return () => clearInterval(interval);
    }, [dispatch]);

    // Cerrar menú/submenú al navegar (evita que quede sobrepuesto)
    useEffect(() => {
        // Solo cerrar submenú al navegar; mantener expandido para poder elegir subitems
        setOpenSubMenu("");
    }, [location.pathname]);

    const SubFlowList = useMemo(() => {
        return flowList
            .filter(flow => userBLIds === null || userBLIds.includes(flow.businessLineId))
            .map(flow => ({
                id: `submenu-flow-${flow.id}`,
                icon: <Icon.ChatSquareText />,
                name: flow.name,
                toLink: `/messaging/flow/${flow.id}`
            }));
    }, [flowList, userBLIds]);




    const SubGestionMensajes = [
        { id: 'submenu-gestion-flow', icon: <Icon.FastForward />, name: 'Flow Builder', toLink: "/flow_messages/builder" },
        { id: 'submenu-gestion-message', icon: <Icon.Whatsapp />, name: 'Mensajes', toLink: "/flow_messages/messages" },
        { id: 'submenu-gestion-analize', icon: <Icon.BarChart />, name: 'Analizar Flujo', toLink: "/flow_messages/analize" },    ];

    const SubGestionAdmin = [
        { id: 'submenu-admin-service', icon: <Icon.Buildings />, name: 'Servicios', toLink: "/admin/services" },
        { id: 'submenu-admin-user', icon: <Icon.PersonBadge />, name: 'Usuarios', toLink: "/admin/users" },
        { id: 'submenu-admin-roles', icon: <Icon.ShieldLock />, name: 'Roles', toLink: "/admin/roles" },
        { id: 'submenu-admin-accounts', icon: <Icon.Telephone />, name: 'Cuentas WSP', toLink: "/admin/accounts" },
        { id: 'submenu-admin-tenants', icon: <Icon.Building />, name: 'Tenants', toLink: "/admin/tenants" },
    ];

    // Administración Mobile (config SaaS de la app del abogado): solo Super-Admin
    const SubAdminMobile = [
        { id: 'submenu-mobile-plans', icon: <Icon.CreditCard />, name: 'Planes de Suscripción', toLink: "/admin-mobile/subscription-plans" },
        { id: 'submenu-mobile-packages', icon: <Icon.Coin />, name: 'Paquetes de Tokens', toLink: "/admin-mobile/token-packages" },
        { id: 'submenu-mobile-commission', icon: <Icon.Percent />, name: 'Comisión', toLink: "/admin-mobile/commission-config" },
    ];

    const SubCampaignes = [
        { id: 'submenu-templates-list', icon: <Icon.PlusCircle />, name: 'Crear Plantilla', toLink: "/campaigns/templates" },
        { id: 'submenu-campaigns-list', icon: <Icon.List />, name: 'Lista de Campañas', toLink: "/campaigns/list" },
        { id: 'submenu-campaigns-report', icon: <Icon.BarChart />, name: 'Analizar Campañas', toLink: "/campaigns/analize" },
    ];


    const _Menu = [
        canAccess('flow_messages') && { id: 'menu-inicio', icon: <Icon.HouseDoor />, name: 'Flujo de Mensajes', toLink: "/flow_messages", subMenu: SubGestionMensajes },
        canAccess('messaging') && { id: 'menu-live', icon: <Icon.ChatDots />, name: 'Vista de Mensajería', toLink: "/messaging", subMenu: SubFlowList },
        window.SHOW_SMART_TALKING && canAccess('smart_talking') && {
            id: 'menu-smart-talking',
            icon: (
                <span style={{ position: 'relative', display: 'inline-flex' }}>
                    <Icon.People />
                    {unreadCount > 0 && (
                        <span style={{
                            position: 'absolute', top: -6, right: -8,
                            background: '#ef4444', color: '#fff',
                            borderRadius: '50%', fontSize: 9, fontWeight: 700,
                            minWidth: 15, height: 15, lineHeight: '15px',
                            textAlign: 'center', padding: '0 3px',
                        }}>
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                </span>
            ),
            name: 'Smart Talking',
            toLink: "/smart_talking",
        },
        canAccess('campaigns') && { id: 'menu-campaigns', icon: <Icon.Flag />, name: 'Campañas', toLink: "/campaigns", subMenu: SubCampaignes },
        canAccess('admin') && { id: 'menu-admin', icon: <Icon.Gear />, name: 'Panel de Administración', toLink: "/admin", subMenu: SubGestionAdmin },
        isSuperAdmin() && { id: 'menu-admin-mobile', icon: <Icon.Phone />, name: 'Administración Mobile', toLink: "/admin-mobile", subMenu: SubAdminMobile },
        canAccess('integracion') && { id: 'menu-integ', icon: <Icon.Boxes />, name: 'Integraciones', toLink: "/integracion" },
        canAccess('base_datos') && { id: 'menu-data', icon: <Icon.Database />, name: 'Base de Conocimiento', toLink: "/base_datos" },
        canAccess('stats') && { id: 'menu-usage', icon: <Icon.GraphUp />, name: 'Estadísticas y Costos', toLink: "/stats" },
    ].filter(Boolean);

    const isActive = (path) => {
        if (path === "#") return false;
        // Match por segmento: "/admin" activa "/admin" y "/admin/..." pero NO "/admin-mobile".
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const handleMenuClick = (id, hasSubMenu) => {
        if (hasSubMenu) {
            // Toggle del submenú
            setOpenSubMenu((prev) => (prev === id ? "" : id));
            setIsExpanded(true);
        } else {
            setIsExpanded(false);
            setOpenSubMenu("");
        }
    };

    const renderTooltip = (props, name) => (
        <Tooltip id={`tooltip-${name}`} {...props}>
            {name}
        </Tooltip>
    );

    const renderNavLink = (item, isSubMenuItem = false) => {
        const link = (
            <NavLink
                to={item.toLink}
                className={`nav-link ${isActive(item.toLink) ? 'active' : ''}`}
                onClick={(e) => {
                    // Si tiene submenú y es item principal: no navegar, solo toggle
                    if (!isSubMenuItem && item.subMenu) {
                        e.preventDefault();
                        handleMenuClick(item.id, true);
                        return;
                    }

                    // Si es subitem o no tiene submenú: navegar y colapsar
                    setIsExpanded(false);
                    setOpenSubMenu("");
                }}
            >
                {item.icon}
                <span className="nav-text">{item.name}</span>
            </NavLink>
        );

        return !isExpanded ? (
            <OverlayTrigger
                placement="right"
                overlay={(props) => renderTooltip(props, item.name)}
            >
                {link}
            </OverlayTrigger>
        ) : link;
    };

    return (
        <div className={`sidebar d-md-block ${isExpanded ? 'expanded' : 'd-none'}`}>
            <button 
                className="toggle-button"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                {isExpanded ? <Icon.ChevronDoubleLeft /> : <Icon.ChevronDoubleRight />}
            </button>
            <Nav className="flex-column">
                {_Menu.map((item) => (
                    <React.Fragment key={item.id}>
                        <Nav.Item>
                            {renderNavLink(item)}
                        </Nav.Item>
                        {item.subMenu && openSubMenu === item.id && isExpanded && (
                            <div className={`submenu ${openSubMenu === item.id ? 'open' : ''}`}>
                                {item.subMenu.map((subItem) => (
                                    <Nav.Item key={subItem.id}>
                                        {renderNavLink(subItem, true)}
                                    </Nav.Item>
                                ))}
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </Nav>
        </div>
    );
};

export default Menu;
